import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SubMenu from "../../../Menu/SubMenu";
import { getData } from "../../../../lib/commonFunctions";
import { initEmperorTentState } from "../../../../store/actions";
import { setSameHeight } from "../../../../lib/elementUtilities";
import {
  ActiveSpell,
  GovernyInfo,
  Spell,
  Upgrade
} from "../../../../lib/types";
import { DragescentState } from "../../../../store/reducers/stateType";
import DialogButton from "../../../Common/DialogButton";
import { UPGRADES } from "../../../../lib/enums";
import { icons } from "../../../../assets/images/icons";
import EmperorTentBanner, { InfoValue } from "../../../Common/EmperorTentBanner";
import InnerLoader from "../../../Common/InnerLoader";
import AvailableSpells from "./AvailableSpells";
import MagicResearch from "./MagicResearch";
import PlannerContent from "./PlannerContent";
import SpellList from "./SpellList";

const MagicEmperorTentSubmenu = () => <SubMenu
  tabs={[
    {
      name: "Magic",
      title: "Magic",
      icon: "magie",
      label: "Selfcast",
      active: false,
      parameters: {
        reload: true
      }
    },
    {
      name: "MagicPlanner",
      title: "Magic",
      icon: "magie",
      label: "Planner",
      active: false,
      parameters: {
        reload: true
      }
    },
    {
      name: "MagicEmperorTent",
      title: "Magic",
      icon: "magie",
      label: "Emperor Tent",
      active: true,
      parameters: {
        reload: false
      }
    }
  ]}
/>

const MagicEmperorTent = () => {
  const govRef = useRef(null);

  const [selectedMember, setSelectedMember] = useState<GovernyInfo | null>(null);
  const id = useSelector((state: DragescentState) => state.governorate.id);
  const race = useSelector((state: DragescentState) => state.governorate.race);
  const alliance = useSelector((state: DragescentState) => state.governorate.alliance);
  const opponent = useSelector((state: DragescentState) => state.governorate.opponent);
  const war = useSelector((state: DragescentState) => state.governorate.war);
  const data = useSelector((state: DragescentState) => state.emperorTent);
  const magicEmperorTentUpgrade = useSelector((state: DragescentState) => state
    .governorate.upgrades.find((u: Upgrade) => u.id === UPGRADES.MagicEmperorTent));
  const magicEmperorTent = useSelector(
    (state: DragescentState) => state.governorate.magicEmperorTent
  );
  const resetSpells = useSelector((state: DragescentState) => state.governorate.upgrades
    .find((u: Upgrade) => u.id === UPGRADES.ResetSpells));
  const credits = useSelector((state: DragescentState) => state.governorate.player.credits);

  const dispatch = useDispatch();

  useEffect(() => {
    if (alliance) {
      getData(`/api/emperorTent/data/magic`)
        .then((emperorTentData) => {
          dispatch(initEmperorTentState(emperorTentData));
          if (!selectedMember) {
            setSelectedMember(emperorTentData.members[0]);
          }
          setSameHeight(govRef.current || undefined);
        });
    }

    return () => {
      dispatch(initEmperorTentState())
    };
  }, []);

  useEffect(() => {
    if (data.members && selectedMember) {
      setSelectedMember(data.members
        .find((member: GovernyInfo) => member.id === selectedMember.id) || null);
    }

    setSameHeight(govRef.current || undefined);
  }, [data]);

  if (!magicEmperorTent) {
    return <>
      <MagicEmperorTentSubmenu/>
      <div className="box">
        <div className="row">
          <div className="col-md-12 text-center">
            <div className="text-bigger">
              Magic emperor tent allows you to manage magic&nbsp;
              (choose, cast and plan spells) across the alliance.
            </div>
            <p>Magic emperor tent not purchased</p>
            <DialogButton
              dialogOptions={{
                name: "PurchaseUpgrade",
                header: "Magic emperor tent",
                id: UPGRADES.MagicEmperorTent,
                params: {},
                question: "Purchase Magic emperor tent?",
                purchasable: !(credits < magicEmperorTentUpgrade.price),
                price: magicEmperorTentUpgrade.price
              }}
            >
              GET <img src={icons.credit} alt=""/>
            </DialogButton>
          </div>
        </div>
      </div>
      <span className="divider"/>
    </>
  }

  if (!alliance) {
    return <>
      <MagicEmperorTentSubmenu />
      <div className="box">
        <div className="row">
          <div className="col-md-12 text-center">
            <div className="text-bigger">
              Army emperor tent can be used only in alliances.
            </div>
          </div>
        </div>
      </div>
      <span className="divider"/>
    </>
  }

  return (
    <>
      <section className="boxes-group">
        <MagicEmperorTentSubmenu />
        {data && data.members && selectedMember ? <>
          <div className="box text-center padding">
            <div className="row persons generals" ref={govRef}>
              {data.members.map(member => <EmperorTentBanner
                id={member.id}
                name={member.name}
                race={member.race}
                info={{
                  [InfoValue.TURNS]: `${member.turn}/${member.maxTurns}`,
                  [InfoValue.ATTACK]: (member.generals?.mage?.general?.attack.sum || 0).toString(),
                  [InfoValue.DEFENCE]: (member.generals?.mage?.general?.defence.sum || 0)
                    .toString(),
                  [InfoValue.GOLD]: `${member.resources.gold}`,
                  [InfoValue.MANA]: `${member.resources.mana}`,
                  [InfoValue.MAGIC_ENERGY]: `${member.resources.magicEnergy}`
                }}
                setUpAction={() => setSelectedMember(member)}
                active={selectedMember?.id === member.id}
              />)}
              {data.members.length < 4 && new Array(4 - data.members.length).fill("").map(() => <EmperorTentBanner
                info={{
                  [InfoValue.TURNS]: "-",
                  [InfoValue.ATTACK]: "-",
                  [InfoValue.DEFENCE]: "-",
                  [InfoValue.GOLD]: "-",
                  [InfoValue.MANA]: "-",
                  [InfoValue.MAGIC_ENERGY]: "-"
                }}
                active={false}
              />)}
            </div>
          </div>

          <section className={selectedMember.race.name} >
          <h2 className="h2 h2-first">
            <span className="bordered">Available spells</span>
          </h2>
          <span className="divider"/>
          <div className="box">
            <div className={"text-info"}>
              <p>
                Learnt <em>{selectedMember.spells.learnt.length}</em> of <em>{
                  selectedMember.spellsLimit
                }</em> spells<br/>
                <em>{selectedMember.spellUpgradeAvailable || 0}</em> upgrades left
              </p>
            </div>
            <AvailableSpells
              govId={selectedMember.id}
              spells={selectedMember.spells}
              spellsLimit={selectedMember.spellsLimit}
              spellUpgradeAvailable={selectedMember.spellUpgradeAvailable}
              magicClosed={selectedMember.magicClosed}
            />
            <div className={"col-sm-6"}>
              <span style={{ paddingRight: "5px" }}>Reset spells</span>
              <DialogButton
                dialogOptions={{
                  name: "PurchaseUpgrade",
                  header: "Reset spells",
                  id: UPGRADES.ResetSpells,
                  params: {
                    govId: selectedMember.id,
                    isOwner: selectedMember.id === id
                  },
                  question: `Reset learnt spells and spell upgrades? ${selectedMember.spells.cast.length
                    ? `<br />You have spell planned for this count. All planned spells will be canceled and spent mana and magic energy returned.`
                    : ""}`,
                  purchasable: !(credits < resetSpells.price),
                  price: resetSpells.price
                }}
              >
                GET <img src={icons.credit} alt=""/>
              </DialogButton>
            </div>
          </div>
            <span className={"divider"} />

          <h2 className="h2 h2-first">
            <span className="bordered">Magic research</span>
          </h2>
          <span className="divider"/>
          <div className="box">
            <MagicResearch magicResearch={selectedMember.magicResearch} govId={selectedMember.id}/>
          </div>

          <h2 className="h2 h2-first">
            <span className="bordered">
              Positive spells on me
            </span>
          </h2>
          <span className="divider"/>
            <SpellList
              govId={selectedMember.id}
              mana={selectedMember.resources.mana}
              availableSpells={selectedMember.spells.available
                .filter((spell: Spell) => spell.buff
                  && (spell.generalSpell || selectedMember.spells.learnt.includes(spell.id)))}
              activeSpells={selectedMember.spells.active
                .filter((spell: ActiveSpell) => spell.spell.buff)}
              autocast={selectedMember.autocast}
              mageGeneral={selectedMember.generals.mage.general}
              disperse={true}
            />

            <PlannerContent
              govId={selectedMember.id}
              castBlockMessage={(!selectedMember.generals.mage.general && "You need mage general to cast the spells.")
              || (!selectedMember.readyToMagic && "You have to be in 8th turn to cast spells outside of governorate.")
              || (!alliance && "Only members of alliances can cast spells out of own governorate.")}
              mana={selectedMember.resources.mana}
              magicEnergy={selectedMember.resources.magicEnergy}
              mageGeneral={selectedMember.generals.mage.general}
              spells={selectedMember.spells}
              spellCastLimit={selectedMember.spellCastLimit}
              magicClosed={selectedMember.magicClosed}
              magicCore={selectedMember.magicCore}
              magicMode={selectedMember.magicMode}
              magicModeAftermath={selectedMember.magicModeAftermath}
              readyToMagic={selectedMember.readyToMagic}
              alliance={alliance}
              war={war}
              opponent={opponent}
            />
          </section>
        </> : <InnerLoader race={race.name} />}
      </section>
    </>
  );
}

export default MagicEmperorTent;
